import React from "react";
import { Document, Page, View, Text, StyleSheet } from "@react-pdf/renderer";
import type { ConstanteData } from "../types/preEmployment.types";
import { getStatusText } from "../utils/preEmploymentUtils";
import StandardPDFLayout from "./StandardPDFLayout";

interface CertificatePDFProps {
  constanteData: ConstanteData;
  selectedStatus: string;
}

const styles = StyleSheet.create({
  page: {
    padding: 15,
    fontSize: 9,
    fontFamily: 'Helvetica',
    backgroundColor: '#ffffff',
  },
  container: { flex: 1, flexDirection: "column", justifyContent: "space-between" },
  centered: { marginTop: 3, marginBottom: 8, alignItems: "center" },
  subtitle: { fontSize: 10, color: "#113285", fontWeight: "bold", marginBottom: 2 },
  number: { fontSize: 9, color: "#6b7280", fontWeight: "bold" },
  section: { marginBottom: 8 },
  sectionTitle: {
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: 5,
    color: "#113285",
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
    borderBottomStyle: "solid",
    paddingBottom: 2,
  },
  row: { flexDirection: "row", marginBottom: 3, alignItems: "flex-start" },
  value: { width: "100%", color: "#374151", fontSize: 9 },
  resultsHeader: { fontSize: 10, fontWeight: "bold", marginBottom: 5, color: "#113285", textAlign: "center", textDecoration: "underline" },
  resultsBox: { backgroundColor: "#f9fafb", padding: 8, borderRadius: 3, borderLeftWidth: 2, borderLeftColor: "#113285" },
  centerNote: { marginBottom: 10, textAlign: "center", fontStyle: "italic", fontSize: 8 },
  signatureSection: {
    marginTop: 15,
    paddingTop: 10,
    borderTop: '1px solid #e5e7eb',
    width: '100%',
  },
  signatureContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 10,
    width: '100%',
  },
  signatureBox: {
    width: '48%',
    textAlign: 'center',
  },
  signatureLine: {
    borderBottom: '1px dashed #000000',
    height: 30,
    marginBottom: 3,
    width: '100%',
  },
  stampBox: {
    width: '48%',
    textAlign: 'center',
  },
  stampArea: {
    height: 30,
    border: '1px dashed #9ca3af',
    borderRadius: 2,
    marginBottom: 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  stampText: {
    fontSize: 7,
    color: '#6b7280',
  },
  signatureLabel: {
    fontSize: 7,
    color: '#4a5568',
    marginTop: 2,
  },
  doctorInfoSection: {
    marginTop: 10,
    textAlign: 'center',
  },
  doctorName: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#113285',
    marginBottom: 1,
  },
  doctorSpecialty: {
    fontSize: 7,
    color: '#4b5563',
    marginBottom: 2,
  },
  signatureDate: {
    fontSize: 7,
    color: '#6b7280',
    marginTop: 2,
  },
});

const CertificateContent: React.FC<CertificatePDFProps> = ({ constanteData, selectedStatus }) => {
  const currentDate = new Date().toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return (
    <StandardPDFLayout
      title="CERTIFICAT MÉDICAL PRÉ-EMBAUCHE"
      documentNumber={constanteData.certificateNumber}
      documentDate={constanteData.examinationDate || new Date().toISOString()}
    >

      <View style={styles.section}>
        <View style={styles.row}>
          <Text style={styles.value}>
            Je soussigné{' '}
            <Text style={{ fontWeight: "bold" }}>
              {constanteData.doctor?.title || ''}{' '}
              {constanteData.doctor?.lastName || 'N/A'}{' '}
              {constanteData.doctor?.firstName || ''}
            </Text>
            , certifie avoir examiné ce jour le{' '}
            <Text style={{ fontWeight: "bold" }}>
              {constanteData.examinationDate || 'N/A'}
            </Text>
            {' '}le/la nommé(e){' '}
            <Text style={{ fontWeight: "bold" }}>
              {constanteData.patient.firstName ? `${constanteData.patient.firstName} ${constanteData.patient.lastName}` : constanteData.patient.lastName}
            </Text>
            , né(e) le{' '}
            <Text style={{ fontWeight: "bold" }}>
              {constanteData.patient?.dateOfBirth || 'N/A'}
            </Text>
            {' '}à{' '}
            <Text style={{ fontWeight: "bold" }}>
              {constanteData.patient?.birthPlace || 'N/A'}
            </Text>.
          </Text>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.resultsHeader}>Résultats de l'examen médical</Text>
        <View style={styles.resultsBox}>
          <Text>{getStatusText(selectedStatus) || "Aucun statut défini"}</Text>
        </View>
      </View>

      <Text style={styles.centerNote}>
        En foi de quoi, ce certificat est délivré pour servir et valoir ce que de droit.
      </Text>

      {/* Signature et cachet - même disposition que l'ordonnance */}
      <View style={{ marginTop: 20, flexDirection: 'row', justifyContent: 'flex-end' }}>
        <View style={{ width: '50%', alignItems: 'flex-end' }}>
          {/* Zone de signature et cachet */}
          <View style={{ marginBottom: 25, width: '100%' }}>
            <Text style={{ fontSize: 6, color: "#4a5568", textAlign: 'right', marginBottom: 3 }}>Signature et cachet du médecin</Text>
          </View>

          {/* Informations du médecin */}
          <View style={{ textAlign: 'right', width: '100%' }}>
            <Text style={{ fontSize: 7, fontWeight: "bold", color: "#113285", marginBottom: 1, textAlign: 'right' }}>
              {constanteData.doctor?.title || ''} {constanteData.doctor?.firstName || ''} {constanteData.doctor?.lastName?.toUpperCase() || 'N/A'}
            </Text>
            <Text style={{ fontSize: 6, color: "#4b5563", marginBottom: 1, textAlign: 'right' }}>Médecin Généraliste</Text>
            <Text style={{ fontSize: 6, color: "#6b7280", marginTop: 1, textAlign: 'right' }}>Fait le {currentDate}</Text>
          </View>
        </View>
      </View>

    </StandardPDFLayout>
  );
};

const CertificatePDF: React.FC<CertificatePDFProps> = (props) => (
  <Document>
    <Page size="A5" style={styles.page}>
      <CertificateContent {...props} />
    </Page>
  </Document>
);

export default CertificatePDF;
